import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, UserCheck, Mail, Clock, X, Trash2 } from "lucide-react";

export default function PendingUsers() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // user being activated
  const [form, setForm] = useState({ company_ids: [], role: "company_manager" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/users/pending"), api.get("/companies")]);
      setItems(p.data); setCompanies(c.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openActivate = (u) => {
    setDlg(u);
    setForm({ company_ids: [], role: "company_manager" });
  };

  const activate = async () => {
    if (form.company_ids.length === 0) return toast.error("اختر شركة واحدة على الأقل");
    try {
      await api.post(`/users/${dlg.id}/activate`, form);
      toast.success(`✅ تم تفعيل ${dlg.name}`);
      setDlg(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reject = async (u) => {
    if (!window.confirm(`رفض حساب "${u.name}" (${u.email}) وحذفه؟`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("تم الرفض والحذف");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="pending-users-page">
      <div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Clock className="w-6 h-6 text-amber-600" /> مستخدمون بانتظار التفعيل ({items.length})</h1>
        <p className="text-sm text-muted-foreground mt-1">مستخدمون سجّلوا الدخول عبر Google ولم يتم ربطهم بشركة بعد</p>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <UserCheck className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا يوجد مستخدمون معلّقون 🎉</div>
        </div>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(u => (
            <div key={u.id} className="border-2 border-amber-200 bg-amber-50/40 rounded-lg p-4" data-testid={`pending-${u.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center"><Users className="w-6 h-6 text-primary" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">سجّل: {new Date(u.created_at).toLocaleString("ar-EG")}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => openActivate(u)} data-testid={`activate-${u.id}`}>
                  <UserCheck className="w-3.5 h-3.5" /> فعّل
                </Button>
                <Button size="sm" variant="outline" className="text-destructive border-red-200 hover:bg-red-50" onClick={() => reject(u)} data-testid={`reject-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
       )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="activate-dialog">
          {dlg && (
            <>
              <DialogHeader><DialogTitle>تفعيل {dlg.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="p-3 rounded bg-muted/30 border text-sm">{dlg.email}</div>
                <div>
                  <Label>الدور</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="activate-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_manager">مدير شركة</SelectItem>
                      <SelectItem value="accountant">محاسب</SelectItem>
                      <SelectItem value="cashier">كاشير</SelectItem>
                      <SelectItem value="sales_rep">مندوب مبيعات</SelectItem>
                      <SelectItem value="warehouse">أمين مستودع</SelectItem>
                      <SelectItem value="auditor">مدقق</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>الشركات المخصصة *</Label>
                  <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                    {companies.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm hover:bg-muted/30 p-1 rounded cursor-pointer">
                        <input type="checkbox" checked={form.company_ids.includes(c.id)} onChange={(e) => {
                          setForm(f => ({ ...f, company_ids: e.target.checked ? [...f.company_ids, c.id] : f.company_ids.filter(x => x !== c.id) }));
                        }} data-testid={`assign-company-${c.id}`} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                    {companies.length === 0 && <p className="text-xs text-muted-foreground p-2">لا توجد شركات — أنشئ واحدة أولاً</p>}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDlg(null)}>إلغاء</Button>
                <Button onClick={activate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-activate-btn">
                  <UserCheck className="w-4 h-4 me-1" /> فعّل الحساب
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
